import { Button, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { CourseTime } from '../../components/CourseTime';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { newClientToken } from '../../lib/client-token';
import { ApiError } from '../../lib/http';
import { remainingSeats } from '../../lib/status';
import { coursesApi } from '../../services/resources';
import type { Course, EnrollmentStatus } from '../../types/api';

export function KioskCoursesPage() {
  const [items, setItems] = useState<Course[]>([]);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState<Course | null>(null);
  const [step, setStep] = useState<'none' | 'notice' | 'confirm' | 'result'>('none');
  const [result, setResult] = useState<EnrollmentStatus | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    coursesApi
      .list({ page: 1, limit: 6, status: 'published' })
      .then((p) => setItems(p.items))
      .catch((e) => setError(e instanceof ApiError ? e.message : '活动列表失败'));
  }, []);

  async function enroll() {
    if (!picked) return;
    setLoading(true);
    try {
      const data = await coursesApi.enroll(picked.id, newClientToken());
      setResult(data.status);
      setStep('result');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '报名失败');
      setStep('none');
    } finally {
      setLoading(false);
    }
  }

  if (error && items.length === 0) return <EmptyState text={error} />;
  if (items.length === 0) return <EmptyState text="本周暂无开放实验课。" />;

  return (
    <>
      <div className="course-grid">
        {items.map((c) => {
          const left = remainingSeats(c);
          return (
            <article key={c.id} className="surface" style={{ padding: 24 }}>
              <h2 className="page-title">{c.title}</h2>
              <CourseTime course={c} compact />
              <p>余位 {left}</p>
              <StatusBadge status={c.status} label={left === 0 ? '已满' : '可报名'} />
              <Button
                type="primary"
                className="kiosk-primary"
                disabled={c.status !== 'published'}
                onClick={() => {
                  setPicked(c);
                  setStep('notice');
                }}
              >
                报名
              </Button>
            </article>
          );
        })}
      </div>
      <Modal open={step === 'notice'} footer={null} onCancel={() => setStep('none')} title="安全须知">
        <p>进入实验室须服从实验员现场安排，不得擅自操作电源与危险器材。</p>
        <div className="row" style={{ gap: 24, marginTop: 24 }}>
          <Button className="kiosk-secondary" onClick={() => setStep('none')}>
            返回
          </Button>
          <Button type="primary" className="kiosk-primary" onClick={() => setStep('confirm')}>
            已阅读
          </Button>
        </div>
      </Modal>
      <Modal
        open={step === 'confirm'}
        footer={null}
        onCancel={() => setStep('none')}
        title={picked ? `确认报名「${picked.title}」？` : '确认报名'}
      >
        {picked ? <CourseTime course={picked} /> : null}
        <div className="row" style={{ gap: 24, marginTop: 24 }}>
          <Button className="kiosk-secondary" onClick={() => setStep('none')}>
            返回
          </Button>
          <Button type="primary" className="kiosk-primary" loading={loading} onClick={() => void enroll()}>
            确认报名
          </Button>
        </div>
      </Modal>
      <Modal open={step === 'result'} footer={null} onCancel={() => setStep('none')} title="报名结果">
        {result === 'waitlisted' ? '已候补' : '已报名'}
        <div style={{ marginTop: 24 }}>
          <Button type="primary" className="kiosk-primary" onClick={() => setStep('none')}>
            关闭
          </Button>
        </div>
      </Modal>
    </>
  );
}
